import {
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration,
  type IPublicClientApplication,
} from '@azure/msal-browser'

import type { AuthConfig } from '@/shared/config/runtime-config'

import { abandonPendingHop, isHopPagePath, isPendingHopExpired, isRoutePath, readPendingHop } from './sign-in-hop'
import { clearSignedInStorage } from './signed-in-storage'

/**
 * Builds and initializes the MSAL client. Not a module singleton: the tenant and client id are
 * unknown until `/config.json` answers. `isHop`: for a tab passing another site's hop through.
 */
export async function createMsalInstance(
  config: AuthConfig,
  origin: string,
  isHop = false,
): Promise<PublicClientApplication> {
  const instance = new PublicClientApplication(buildMsalConfiguration(config, origin, isHop))

  // MSAL v3+: every other call throws "uninitialized_public_client_application" until this resolves.
  await instance.initialize()

  return instance
}

/**
 * Consumes any sign-in response in the URL before first render and makes its account active. A
 * response that fails signs nobody out: the cache is every tab's, so a canceled or expired renewal
 * leaves the person signed in as they were, and the failure page shows why; an account whose token
 * can no longer be renewed is sent through sign-in again by the guard.
 * For another site's hop (`isHop`, SM-04) this site's own sign-in stays as it was, failed or not:
 * the hop's account is never made active, and its result is answered for the hop to use.
 */
export async function consumeSignInResponse(
  instance: IPublicClientApplication | null,
  isHop = false,
): Promise<AuthenticationResult | null> {
  if (instance === null) {
    return null
  }

  let result: AuthenticationResult | null
  try {
    // `false`: otherwise MSAL navigates back to /login before AuthCallbackPage can run.
    result = await instance.handleRedirectPromise({ navigateToLoginRequestUrl: false })
  } catch (error: unknown) {
    // Signed out in another tab meanwhile: what this tab kept for that person goes too.
    if (!isHop && instance.getAllAccounts().length === 0) {
      clearSignedInStorage()
    }
    throw error
  }
  if (isHop) {
    return result
  }
  const account = result?.account ?? instance.getAllAccounts()[0] ?? null
  if (account !== null) {
    // Otherwise every acquireTokenSilent call must name the account.
    instance.setActiveAccount(account)
  }
  return result
}

/**
 * On the sign-in site, once another site's hop finished or failed in this tab: forgets every
 * account, token and in-progress state MSAL kept for it in this tab's session storage, the only
 * place a hop's tab keeps them (see {@link buildMsalConfiguration}). The sign-in site only passes
 * the sign-in on; it never keeps one of its own (Aaron Mulder, 2026-10-30).
 */
export async function forgetHopTabSignIn(instance: IPublicClientApplication | null): Promise<void> {
  // Never on a client whose cache this site's tabs share: their own sign-in stays.
  if (instance === null || instance.getConfiguration().cache.cacheLocation !== 'sessionStorage') {
    return
  }
  try {
    await instance.clearCache()
  } catch {
    // Removed below all the same.
  }
  forgetHopTabStorage()
}

/**
 * Whether this tab passes another site's hop through, so MSAL keeps its cache in this tab alone: on
 * the hop page, or with a hop pending. A pending hop too old to finish no longer counts, and is
 * forgotten, except on the callback, where its B2C answer may still come back and must never reach
 * the cache this site's tabs share.
 */
export function isHopTab(redirectPath: string, pathname: string = window.location.pathname): boolean {
  const pending = readPendingHop()
  if (pending !== null && isPendingHopExpired(pending) && !isRoutePath(redirectPath, pathname)) {
    abandonPendingHop()
    forgetHopTabStorage()
  }
  return isHopPagePath(pathname) || readPendingHop() !== null
}

/**
 * On a configuration failure in a tab another site's hop passes through: nothing of the hop is
 * kept, B2C's answer in the address included, so a reload never finishes it on the shared cache.
 */
export function forgetFailedHopTab(): void {
  if (readPendingHop() === null) {
    return
  }
  abandonPendingHop()
  forgetHopTabStorage()
  window.history.replaceState(window.history.state, '', window.location.pathname)
}

/**
 * Removes everything MSAL keeps in this tab's session storage. Only for a tab a hop passed through:
 * elsewhere it would drop the state of this site's own sign-in under way in the tab.
 */
export function forgetHopTabStorage(): void {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('msal.')) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Storage blocked: nothing was kept.
  }
}

/**
 * Maps the runtime settings to MSAL configuration. Exported for testing without a client. `isHop`
 * keeps the cache in this tab alone, so another site's sign-in is never in the cache this site's
 * tabs share, where one of them could take it for its own (SM-04).
 */
export function buildMsalConfiguration(config: AuthConfig, origin: string, isHop = false): Configuration {
  // B2C accepts no wildcard redirect URIs: every value this produces must be registered by hand.
  const redirectUri = new URL(config.redirectPath, origin).toString()

  return {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      // MSAL cannot infer trust for a B2C host; without this sign-in fails "untrusted_authority".
      knownAuthorities: [config.knownAuthority],
      redirectUri,
      // Already registered in B2C, and the callback forwards signed-out visitors to /login.
      postLogoutRedirectUri: redirectUri,
    },
    cache: {
      // Shared by every tab, as Aaron Mulder decided (ADR-0001 §9). MSAL encrypts it with a key
      // that ends with the browser session; signing out clears it for every tab. A hop's tab keeps its own.
      cacheLocation: isHop ? 'sessionStorage' : 'localStorage',
    },
  }
}
